import React from 'react'
import { func, string } from 'prop-types'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { TextField, List, ListItem, useZionFormContext } from '@fs/zion-ui'
import Debouncer from './debouncer'
import fetchPlaceOptions from './fetchPlaceOptions'

const borderedListCss = css`
  background: #fff;
  border: 1px solid #aaaaaa;
  position: absolute;
  z-index: 1;
  height: 200px;
  overflow: auto;
  top: 70px;
  width: 100%;
`

const PLACE_SEARCH_REQUEST_INTERVAL = 500
const debouncer = Debouncer(PLACE_SEARCH_REQUEST_INTERVAL)

export default function PlaceInput({ fieldName, idFieldName, fieldValue, idFieldValue, label, onOptionSelected }) {
  const [t] = useTranslation()
  const [inputText, setInputText] = React.useState(fieldValue || '')
  const [inputId, setInputId] = React.useState(idFieldValue || '')

  const [showList, setShowList] = React.useState(false)
  const [options, setOptions] = React.useState([])
  const [debouncedSearchTerm, setDebouncedSearchTerm] = React.useState('')

  const [selectedOption, setSelectedOption] = React.useState({ primary: fieldValue, placeId: idFieldValue })

  const useOutsideClickNotifier = (ref) => {
    const handleClickOutside = (event) => {
      if (ref.current && !ref.current.contains(event.target)) {
        setShowList(false)
      }
    }
    React.useEffect(() => {
      document.addEventListener('mousedown', handleClickOutside)
      return () => {
        document.removeEventListener('mousedown', handleClickOutside)
      }
    })
  }
  const clickRef = React.useRef(null)
  useOutsideClickNotifier(clickRef)

  React.useEffect(() => {
    if (fieldValue && idFieldValue) {
      setSelectedOption({ primary: fieldValue, placeId: idFieldValue })
      setInputText(fieldValue)
    } else {
      setInputText(null)
    }
  }, [fieldValue, idFieldValue])

  const { makeFormComponent } = useZionFormContext()

  debouncer.debounce(inputText, setDebouncedSearchTerm)

  React.useEffect(() => {
    setOptions([])
  }, [inputText])

  React.useEffect(() => {
    const hasSearchTerm = !!debouncedSearchTerm && debouncedSearchTerm.trim() !== ''
    const hasPlaceId = !!selectedOption.placeId
    const needsLookup = hasSearchTerm && !hasPlaceId

    if (needsLookup) {
      fetchPlaceOptions(debouncedSearchTerm).then((response) => {
        if (response) {
          setOptions(response)
          if (inputText !== selectedOption.primary) {
            setShowList(true)
          }
        }
      })
    } else {
      setOptions([])
    }
  }, [debouncedSearchTerm, inputText, selectedOption, selectedOption.placeId, selectedOption.primary])

  return makeFormComponent(
    <>
      <TextField
        autocomplete="off"
        label={label}
        name={fieldName}
        onChange={(e) => {
          setInputText(e.target.value)
          if (selectedOption.primary) {
            setSelectedOption({})
            onOptionSelected({})
          }
        }}
        placeholder={t('atf.AncestorEditor.placeholders.place', 'Search for a place')}
        value={inputText}
      />
      <input type="hidden" value={inputId} name={idFieldName} />

      {showList && options.length > 0 && (
        <div css={borderedListCss} ref={clickRef}>
          <List>
            {options.map((el) => (
              <ListItem
                key={el.key}
                primaryText={el.primary}
                secondaryText={el.secondary}
                onClick={() => {
                  const selected = options.find((opt) => opt.placeId === el.key)
                  setSelectedOption(selected)
                  onOptionSelected(selected)

                  setInputText(selected.primary)
                  setInputId(selected.placeId)

                  setShowList(false)
                }}
              />
            ))}
          </List>
        </div>
      )}
    </>
  )
}

PlaceInput.propTypes = {
  fieldName: string,
  idFieldName: string,
  fieldValue: string,
  idFieldValue: string,
  label: string,
  onOptionSelected: func,
}
